import {
  default as ECharts,
  default as EChartsClient,
} from '#/components/ECharts';
import { useTranslation } from '#/contexts/useTranslation';
import useTotalStakedValue from '#/data/useTotalStakedValue';
import { format } from 'date-fns';
import { EChartsOption, graphic } from 'echarts';
import { ethers } from 'ethers';
import { useMemo } from 'react';

export function PortfolioValueHistoryChart() {
  const translation = useTranslation();
  const { data } = useTotalStakedValue(7);
  const dataSource = useMemo(() => data?.data.data ?? [], [data]);
  const seriesData = useMemo(
    () =>
      dataSource.map(item => ({
        name: format(item.date, 'MM-dd'),
        value: ethers.FixedNumber.from(item.totalDelegatedUSD)
          // .addUnsafe(ethers.FixedNumber.from(item.totalRewardUSD))
          .round(2)
          .toUnsafeFloat(),
      })),
    [dataSource]
  );
  // 如果每一天数据都是 0 就表示没有数据
  const isEmpty = useMemo(
    () => seriesData.every(item => item.value === 0),
    [seriesData]
  );
  const xAxisData = useMemo(
    () => seriesData.map(item => item.name),
    [seriesData]
  );
  const option = useMemo<EChartsOption>(
    () => ({
      grid: {
        top: 16,
        right: 32,
        bottom: 0,
        left: 0,
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: xAxisData,
        axisLabel: {
          fontSize: '0.875rem',
        },
      },
      yAxis: {
        type: 'value',
        axisLabel: {
          fontSize: '0.875rem',
          formatter: (value: number, index: number) => {
            if (value >= 10_000) {
              return `${ethers.utils.commify((value / 1_000) >> 0)}K`;
            } else {
              return `${ethers.utils.commify(value)}`;
            }
          },
        },
        splitNumber: 4,
        splitLine: {
          lineStyle: {
            type: 'dashed',
          },
        },
        min: function (value) {
          return Math.max(
            Math.ceil(value.min - Math.max((value.max - value.min) / 4, 1)),
            0
          );
        },
      },
      tooltip: {
        trigger: 'axis',
        // formatter: '{b}: ${c}',
        formatter: ([params]: any) => {
          return `${params.name}: <br/>$${ethers.utils.commify(params.value)}`;
        },
      },
      series: [
        {
          data: seriesData,
          type: 'line',
          showSymbol: false,
          lineStyle: {
            color: '#4473F0',
            width: 2,
          },
          areaStyle: {
            color: new graphic.LinearGradient(0, 0, 0, 1, [
              {
                offset: 0,
                color: 'rgba(68, 115, 240, 0.2)',
              },
              {
                offset: 1,
                color: 'rgba(68, 115, 240, 0)',
              },
            ]),
          },
        },
      ],
    }),
    [seriesData]
  );
  return (
    <section className="bg-white dark:bg-gray-700 border border-slate-200 rounded-2xl p-4 @3xl/main:p-6 space-y-4 flex flex-col h-full">
      <header className="flex items-center space-x-0.5">
        <span className="text-sm @3xl/main:text-xl font-bold truncate max-w-full inline-block">
          {translation.PortfolioDashboard.PortfolioValueToUSDChart.Heading}
        </span>
        {/* <QuestionTooltip
          content="This chart is updated daily at 22:00 GMT +8"
          className="inline-block cursor-pointer shrink-0"
        /> */}
      </header>
      <article className="flex-auto overflow-auto">
        {seriesData.length === 0 || isEmpty ? (
          <SkeletonLineChart className="w-full h-full pointer-events-none min-w-[400px]" />
        ) : (
          <EChartsClient
            option={option}
            className="w-full h-full min-w-[400px]"
          />
        )}
      </article>
      <span className="text-xs text-gray-500 text-center">
        {translation.PortfolioDashboard.PortfolioValueToUSDChart.Description}
      </span>
    </section>
  );
}

interface SkeletonLineChartProps {
  className?: string;
}
function SkeletonLineChart({ className }: SkeletonLineChartProps) {
  const option = useMemo<EChartsOption>(
    () => ({
      grid: {
        top: 16,
        right: 32,
        bottom: 0,
        left: 0,
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        axisLabel: {
          fontSize: '0.75rem',
          color: '#E2E8F0',
          formatter: () => `██`,
        },
      },
      yAxis: {
        type: 'value',
        axisLabel: {
          fontSize: '0.75rem',
          color: '#E2E8F0',
          formatter: () => `████`,
        },
        splitNumber: 4,
        splitLine: {
          lineStyle: {
            type: 'dashed',
          },
        },
      },
      series: [
        {
          data: [
            { value: 248, name: '1' },
            { value: 95, name: '2' },
            { value: 230, name: '3' },
            { value: 284, name: '4' },
            { value: 224, name: '5' },
            { value: 384, name: '6' },
            { value: 348, name: '7' },
          ],
          type: 'line',
          showSymbol: false,
          lineStyle: {
            color: '#A0AEC0',
            width: 2,
          },
          areaStyle: {
            color: new graphic.LinearGradient(0, 0, 0, 1, [
              {
                offset: 0,
                color: 'rgba(226, 232, 240, 0.5)',
              },
              {
                offset: 1,
                color: 'rgba(226, 232, 240, 0)',
              },
            ]),
          },
        },
      ],
    }),
    []
  );
  return <ECharts option={option} className={className} />;
}
